import { FC, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { EstadoDeLista, EstadoDeListaTipo } from '@/components/common/EstadoDeLista'
import { nivelConsultorService } from '@/services/nivelConsultorService'
import { AUTOR_PAPEL_ROTULO } from '@/types/campanha'
import { AtribuicaoNivelLinha, HistoricoNivel } from '@/types/nivelConsultor'
import { formatDataHoraNivel } from './formatacao'

export interface HistoricoNivelDrawerProps {
  /** Consultor; `null` = fechado. */
  linha: AtribuicaoNivelLinha | null
  onFechar: () => void
}

const estadoDoErro = (e: unknown): EstadoDeListaTipo =>
  (e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro'

/**
 * Histórico de atribuição de um consultor (ADMIN — AN-10 §4/§10): data, de → para (nomes
 * congelados da época), justificativa e autor. Append-only no backend; aqui é só leitura.
 */
export const HistoricoNivelDrawer: FC<HistoricoNivelDrawerProps> = ({ linha, onFechar }) => {
  const consultorId = linha?.consultorId
  const [itens, setItens] = useState<HistoricoNivel[]>([])
  const [estado, setEstado] = useState<EstadoDeListaTipo | null>('carregando')

  const carregar = useCallback(async () => {
    if (!consultorId) return
    setEstado('carregando')
    try {
      const lista = await nivelConsultorService.historico(consultorId)
      setItens(lista)
      setEstado(lista.length === 0 ? 'vazio' : null)
    } catch (e) {
      setItens([])
      setEstado(estadoDoErro(e))
    }
  }, [consultorId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  return (
    <Drawer
      anchor="right"
      open={!!linha}
      onClose={onFechar}
      PaperProps={{ role: 'dialog', 'aria-label': 'Histórico de nível', sx: { width: { xs: '100%', sm: 640 }, p: 3 } }}
    >
      {linha && (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="overline" sx={{ fontWeight: 700 }} color="primary">
                Histórico de nível
              </Typography>
              <Typography variant="h6">{linha.nome || linha.consultorId}</Typography>
            </Box>
            <IconButton aria-label="Fechar" onClick={onFechar}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {estado ? (
            <EstadoDeLista
              estado={estado}
              minHeight={160}
              mensagem={estado === 'vazio' ? 'Nenhuma alteração de nível registrada' : undefined}
              {...(estado === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void carregar() } } : {})}
            />
          ) : (
            <TableContainer>
              <Table size="small" aria-label="Histórico de nível do consultor">
                <TableHead>
                  <TableRow>
                    <TableCell>Quando</TableCell>
                    <TableCell>De → Para</TableCell>
                    <TableCell>Justificativa</TableCell>
                    <TableCell>Autor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itens.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDataHoraNivel(h.criadoEm)}</TableCell>
                      <TableCell>
                        {h.nivelAnteriorNome ?? 'Sem nível'} → {h.nivelNovoNome ?? 'Sem nível'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{h.justificativa}</TableCell>
                      <TableCell>{h.autorNome || AUTOR_PAPEL_ROTULO[h.autorPapel] || h.autorPapel}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      )}
    </Drawer>
  )
}

export default HistoricoNivelDrawer
